// src/cockpit4/components/LoginModal.tsx
// ============================================
// LoginModal - 内嵌登录弹窗（v6.39 游客解锁）
//
// 用途：游客在「毛玻璃预览遮罩」或「财务/设置加密锁」中点击解锁时弹出，
//       使用现有账号密码（super_admin / employee）登录。
//       登录成功后 currentUser.role 变化 → 外层遮罩/加密锁自动卸载，内容解锁。
//
// 安全：与 LoginPanel 一致——必须严格输入用户名/密码，无任何一键登录绕过。
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, User, Eye, EyeOff, AlertCircle, ShieldCheck } from "lucide-react";
import { useUserStore } from "../../stores/userStore";

export default function LoginModal({ onClose }: { onClose: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useUserStore();

  // 登录：严格使用输入框值，禁止任何一键登录/绕过机制
  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError("请输入用户名和密码");
      return;
    }
    setLoading(true);
    setError("");
    // 模拟短暂加载（避免暴力破解感知）
    await new Promise((r) => setTimeout(r, 300));
    const result = await login({ username, password });
    if (!result.success) {
      setError(result.error || "登录失败");
      setLoading(false);
      return;
    }
    // 成功：currentUser 更新 → 外层遮罩/加密锁因角色变化自动卸载，无需手动关闭
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm mx-4 rounded-2xl border border-white/[0.08] bg-[#0F1520]/95 backdrop-blur-2xl p-6 shadow-2xl"
      >
        {/* 头部 */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-blue-500/30 flex items-center justify-center">
            <ShieldCheck size={18} className="text-blue-400" />
          </div>
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold text-white tracking-tight">解锁完整数据</span>
            <span className="text-[10px] font-mono text-gray-500">XEAOS 授权登录</span>
          </div>
          <button
            onClick={onClose}
            className="ml-auto w-7 h-7 rounded-md text-gray-500 hover:text-gray-300 hover:bg-white/[0.06] text-[12px] transition-colors"
          >
            ✕
          </button>
        </div>

        {/* 用户名 */}
        <label className="block text-[10px] font-mono text-gray-400 mb-1.5 uppercase tracking-wider">
          用户名 / 手机号
        </label>
        <div className="relative mb-3">
          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="输入用户名或手机号"
            autoComplete="off"
            name="loginmodal-username-off"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/[0.08] text-white text-[13px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors"
          />
        </div>

        {/* 密码 */}
        <label className="block text-[10px] font-mono text-gray-400 mb-1.5 uppercase tracking-wider">
          密码
        </label>
        <div className="relative mb-4">
          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="输入密码"
            autoComplete="new-password"
            name="loginmodal-password-off"
            className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-black/30 border border-white/[0.08] text-white text-[13px] placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
          >
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>

        {/* 错误提示 */}
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20"
          >
            <AlertCircle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-[11px] text-red-300">{error}</span>
          </motion.div>
        )}

        {/* 登录按钮 */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-[13px] font-medium hover:from-blue-400 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
        >
          {loading ? "登录中..." : "解锁并进入"}
        </button>

        <p className="mt-3 text-center text-[9px] font-mono text-gray-600">
          仅授权用户可查看 · 凭据错误将被拒绝
        </p>
      </motion.div>
    </div>
  );
}
